import { forwardRef, type SelectHTMLAttributes, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  hasError?: boolean;
  /** Children sind <option>-Elements */
  children: ReactNode;
};

/**
 * Custom-styled Select.
 * Native <select> mit Browser-Default-Pfeil unterdrückt + lucide ChevronDown overlay.
 *
 * Begründung: kein eigener Listbox-Build = volle a11y + Mobile-System-Picker.
 *
 * @example
 *   <Select defaultValue="general">
 *     <option value="general">Allgemein</option>
 *     <option value="reservation">Reservierung</option>
 *   </Select>
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { hasError, className, children, ...props },
  ref,
) {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          'w-full appearance-none rounded-md',
          'bg-paper-2 text-ink-2',
          'font-sans text-base',
          'h-11 pl-3.5 pr-10',
          'cursor-pointer',
          hasError
            ? 'border border-it-red focus:outline-2 focus:outline-it-red'
            : 'border border-rule focus:border-gold focus:outline-2 focus:outline-gold/40 focus:outline-offset-1',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'transition-colors',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-4 text-ink-mute"
        aria-hidden="true"
      />
    </div>
  );
});
